import { GetCategoryVariableColorMapResponse } from '@/api/dashboards/getCategoryVariableColorMap';
import { groupSpendSummaryData } from '@/dashboards/SpendSummary/helpers/groupSpendSummaryData';
import {
    FormattedSpendSummaryData,
    GetSpendSummaryWithRoiResponse,
} from '@/dashboards/SpendSummary/types';
import { DashboardVariable } from '@/dashboards/types';

export const createEmptySpendSummaryData = (): FormattedSpendSummaryData => ({
    contributionDataByVariable: new Map(),
    contributionDataByCategory: new Map(),
    spendDataByVariable: new Map(),
    spendDataByCategory: new Map(),
    totalContributionProductByVariable: new Map(),
    totalContributionProductByCategory: new Map(),
    totalContribution: 0,
    totalSpend: 0,
    totalContributionProduct: 0,
    totalContributionByCategory: new Map(),
    totalContributionByVariable: new Map(),
    totalSpendByCategory: new Map(),
    totalSpendByVariable: new Map(),
    variableToCategory: new Map(),
    variableData: [],
    categoryData: [],
    dates: [],
    treeNodes: [],
});

export interface PrepareSpendSummaryDataParams {
    rows?: GetSpendSummaryWithRoiResponse['rows'];
    dashboardVariables: DashboardVariable[];
    categoryColorMap: GetCategoryVariableColorMapResponse['categoryColorMap'];
    variableColorMap: GetCategoryVariableColorMapResponse['variableColorMap'];
}

/**
 * Prepares and aggregates spend summary data by filtering out competitor variables
 * and extracting output-only modeled variables.
 */
export const prepareSpendSummaryData = ({
    rows,
    dashboardVariables,
    categoryColorMap,
    variableColorMap,
}: PrepareSpendSummaryDataParams): FormattedSpendSummaryData => {
    if (!rows || rows.length === 0) {
        return createEmptySpendSummaryData();
    }

    const competitorVariableSet = new Set<string>();
    const modeledDashboardVariables: DashboardVariable[] = [];

    for (const variable of dashboardVariables) {
        if (variable.competitor) {
            competitorVariableSet.add(variable.variableName);
        }
        if (variable.fileType === 'output') {
            modeledDashboardVariables.push(variable);
        }
    }

    /**
     * Competitor data might be modeled in the dashboard to display the impact of the competition on the model.
     * Competitor variables might include the spend of competitor, therefore it will be included in this list.
     * We have to filter them out from this view whatsoever.
     */
    const filteredRows = rows.filter(
        (row) => !competitorVariableSet.has(row.variable),
    );

    return groupSpendSummaryData({
        rows: filteredRows,
        categoryColorMap,
        variableColorMap,
        dashboardVariables: modeledDashboardVariables,
    });
};
